/* ==========================================================================
 * HOW TO ADD A NEW PROJECT
 * --------------------------------------------------------------------------
 * 1. Copy any entry of the PROJECTS array below and paste it at the end.
 * 2. Set `id` to the project id coming from the API (the dropdown sends this
 *    exact value) and give it a short `name` used as the map layer label.
 * 3. Paste the outline into `polygons` as [longitude, latitude] pairs. One
 *    array per shape - a project may have several shapes (see Fuzuli).
 * 4. Set `center` (where the map flies when the project is picked) and `info`
 *    (the popup content, or `null` when the project has no popup).
 * Nothing else has to change: sources, layers, fly-to and popup are all built
 * from this array. Every project automatically gets its own fill colour from
 * PALETTE (in order); add `color: '#XXXXXX'` to an entry to pin one instead.
 * Polygons are clickable out of the box: a click selects the project and
 * syncs the dropdown + info popup - no wiring needed per project.
 * ========================================================================== */

import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl';
import { Link } from 'react-router-dom';

const MAPBOX_TOKEN = 'pk.eyJ1Ijoib2ZlbGlhcnYiLCJhIjoiY20xejJ1dWhhMDJpZzJpczhzMnJvZjdmMyJ9.yXFL1Nfhvw2sld9yPx8tLA';
const MAP_STYLE = 'mapbox://styles/mapbox/satellite-v9';
const DEFAULT_CENTER = [47.912, 40.173];
const DEFAULT_ZOOM = 7;
const PROJECT_ZOOM = 14;
// One fill colour per project, assigned in PROJECTS order (wraps around past 15).
// Bright hues chosen to stay readable at 0.6 opacity over satellite imagery.
const PALETTE = [
  '#FFB900', // amber
  '#FF3B30', // red
  '#00C7BE', // teal
  '#AF52DE', // purple
  '#FF9500', // orange
  '#32D74B', // green
  '#0A84FF', // blue
  '#FF2D95', // pink
  '#FFD60A', // yellow
  '#5E5CE6', // indigo
  '#66D4CF', // cyan
  '#FF6482', // coral
  '#ADFF2F', // lime
  '#DA70D6', // orchid
  '#40C8E0', // sky
];
const FILL_OPACITY = 0.6;

const PROJECTS = [
  {
    id: '300004',
    name: 'Yasamal',
    center: [49.799079, 40.375360],
    polygons: [
      [
        [49.795371, 40.377844],
        [49.795509, 40.378016],
        [49.795495, 40.378160],
        [49.795905, 40.378309],
        [49.796834, 40.378354],
        [49.797776, 40.378400],
        [49.798427, 40.376875],
        [49.799079, 40.375360],
        [49.797651, 40.375480],
        [49.796212, 40.375609],
        [49.795906, 40.374921],
        [49.795070, 40.374904],
        [49.794234, 40.374895],
        [49.794361, 40.375680],
        [49.793143, 40.375965]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 11,6 hektar <br />
        Yaşayış binalarının ümumi sayı – 29<br />
        12 mərtəbəli – 12 bina<br />
        9 mərtəbəli – 17 bina<br />
        Mənzillərin ümumi sayı – 1 843<br />
        1 otaqlı – 324 <br />
        2 otaqlı – 1052 <br />
        3 otaqlı – 467 <br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 31,5 - 33,37 m² <br />
        2 otaqlı – 49,77 - 57,87 m²<br />
        3 otaqlı – 68,86 - 70,46 m²<br />
        1 ümumtəhsil məktəbi (960 yerlik)<br />
        1 uşaq bağçası (240 yerlik)
      </p>
    ),
  },
  {
    id: '100001',
    name: 'Yasamal 2',
    center: [49.795442, 40.371232],
    polygons: [
      [
        [49.799553, 40.375166],
        [49.799958, 40.374261],
        [49.801283, 40.372599],
        [49.796549, 40.371216],
        [49.795703, 40.371117],
        [49.795442, 40.371232],
        [49.795318, 40.372240],
        [49.795951, 40.372408],
        [49.795987, 40.374985],
        [49.796393, 40.375350],
        [49.796722, 40.375416]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 15,4 hektar <br />
        Yaşayış binalarının ümumi sayı – 40<br />
        9 mərtəbəli – 22 bina<br />
        12 mərtəbəli – 18 bina<br />
        Mənzillərin ümumi sayı – 2 200<br />
        1 otaqlı – 144<br />
        Studio tipli – 156<br />
        2 otaqlı – 752<br />
        3 otaqlı – 969<br />
        4 otaqlı – 176<br />
        5 otaqlı – 2<br />
        6 otaqlı – 1<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 35,46 - 35,57 m²<br />
        Studio tipli – 33,52 - 35,00 m²<br />
        2 otaqlı – 55,91 - 60,02 m²<br />
        3 otaqlı – 72,55 - 76,79 m²<br />
        4 otaqlı – 96,10 - 96,67 m²<br />
        5 otaqlı – 142,72 - 142,78 m²<br />
        6 otaqlı – 178,64 m²<br />
        1 ümumtəhsil məktəbi (1 200 yerlik)<br />
        2 ədəd (125 yerlik), 1 ədəd (100 yerlik) uşaq bağçası
      </p>
    ),
  },
  {
    id: '300001',
    name: 'Hövsan',
    center: [50.066448, 40.378125],
    polygons: [
      [
        [50.073522, 40.381974],
        [50.074532, 40.378793],
        [50.066448, 40.378125],
        [50.067925, 40.381200]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 20 hektar<br />
        Yaşayış binalarının ümumi sayı – 11<br />
        7 mərtəbəli – 7 bina<br />
        10 mərtəbəli – 4 bina<br />
        Mənzillərin ümumi sayı – 2 962<br />
        1 otaqlı – 280<br />
        2 otaqlı  – 1 401<br />
        3 otaqlı – 980<br />
        4 otaqlı – 301<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 37,30 - 38,70 m²<br />
        2 otaqlı  – 49,22 - 67,39 m²<br />
        3 otaqlı – 68,13 - 95,02 m²<br />
        4 otaqlı – 78,67 - 106,18 m²<br />
        2 uşaq bağçası (90 yerlik)<br />
        1 ümumtəhsil məktəbi (1 920 yerlik)<br />
        1 tibb mərkəzi
      </p>
    ),
  },
  {
    id: '300005',
    name: 'Hövsan 2',
    center: [50.069394, 40.384067],
    polygons: [
      [
        [50.072542, 40.389057],
        [50.073576, 40.384065],
        [50.069047, 40.383569],
        [50.069394, 40.384067],
        [50.068624, 40.385178],
        [50.067185, 40.386203]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 18,81 hektar<br />
        Yaşayış binalarının ümumi sayı – 60<br />
        9 mərtəbəli – 36 bina<br />
        12  mərtəbəli – 24 bina<br />
        Mənzillərin ümumi sayı – 2 950<br />
        Studio tipli – 192<br />
        2 otaqlı – 414<br />
        3 otaqlı – 1 722<br />
        4 otaqlı – 606<br />
        5 otaqlı – 16<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 40,1 m²<br />
        2 otaqlı – 57,68 - 57,70 m²<br />
        3 otaqlı – 72,82 - 76,71 m²<br />
        4 otaqlı – 98,7 - 114,61 m²<br />
        5 otaqlı – 156,48 - 156,52 m²<br />
        1 ümumtəhsil məktəbi (1 920 yerlik)<br />
        1 uşaq bağçası (240 yerlik)
      </p>
    ),
  },
  {
    id: '1',
    name: 'Gəncə',
    center: [46.375786, 40.681261],
    polygons: [
      [
        [46.376378, 40.683605],
        [46.377880, 40.682416],
        [46.376371, 40.680921],
        [46.375786, 40.681261],
        [46.374747, 40.680229],
        [46.373402, 40.681019]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 6,23 hektar<br />
        Yaşayış binalarının ümumi sayı – 18<br />
        9 mərtəbəli – 12 bina<br />
        12 mərtəbəli – 6 bina<br />
        Mənzillərin ümumi sayı – 986<br />
        1 otaqlı – 48<br />
        Studio tipli – 68<br />
        2 otaqlı – 318<br />
        3 otaqlı – 466<br />
        4 otaqlı – 86<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 35,46 – 35,57 m²<br />
        Studio tipli – 34,85 - 34,97 m²<br />
        2 otaqlı – 56,40 - 56,86 m²<br />
        3 otaqlı – 72,55 - 76,79 m²<br />
        4 otaqlı – 96,10 - 96,67 m²<br />
        1 ümumtəhsil məktəbi (1 176 yerlik)<br />
        1 uşaq bağçası (125 yerlik)
      </p>
    ),
  },
  {
    id: '300017',
    name: 'Sumqayit',
    center: [49.675748, 40.605229],
    polygons: [
      [
        [49.674422, 40.604737],
        [49.675724, 40.605256],
        [49.675748, 40.605229],
        [49.676587, 40.604023],
        [49.675369, 40.603468]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 2 hektar<br />
        Yaşayış binalarının ümumi sayı – 11<br />
        9 mərtəbəli – 6 bina<br />
        12 mərtəbəli – 4 bina<br />
        15 mərtəbəli – 1 bina<br />
        Mənzillərin ümumi sayı – 461<br />
        Studio tipli – 13<br />
        2 otaqlı – 113<br />
        3 otaqlı – 279<br />
        4 otaqlı – 56<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 50,2 m²<br />
        2 otaqlı – 59,7 - 59,9 m²<br />
        3 otaqlı – 77,1 - 82,4 m²<br />
        4 otaqlı – 104,4 - 108,3 m²<br />
        1 uşaq bağçası (80 yerlik)
      </p>
    ),
  },
  {
    id: '200002',
    name: 'Sumqayit 2',
    center: [49.667094, 40.580933],
    polygons: [
      [
        [49.667565, 40.581614],
        [49.668231, 40.580820],
        [49.667616, 40.580236],
        [49.667094, 40.580933],
        [49.667422, 40.581072],
        [49.667143, 40.581438]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 0,75 hektar <br />
        Yaşayış binalarının ümumi sayı – 2<br />
        9 mərtəbəli – 2 bina<br />
        Mənzillərin ümumi sayı – 144<br />
        Studio tipli – 18 <br />
        1 otaqlı – 18<br />
        2 otaqlı – 72<br />
        3 otaqlı – 36<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 34,83 m²<br />
        1 otaqlı – 35,56 m² <br />
        2 otaqlı – 56,53 - 56,62 m²<br />
        3 otaqlı – 74,43 - 74,57 m²<br />
      </p>
    ),
  },
  {
    id: '300016',
    name: 'Lankaran',
    center: [48.839397, 38.768968],
    polygons: [
      [
        [48.840196, 38.770326],
        [48.840603, 38.769432],
        [48.839513, 38.769304],
        [48.839397, 38.768968],
        [48.839212, 38.768992],
        [48.838920, 38.769131],
        [48.838588, 38.769458],
        [48.838192, 38.769622]
      ]
    ],
    info: (
      <p style={{ lineHeight: '1.7rem' }}>
        Ərazinin ümumi sahəsi – 1,5 hektar<br />
        Yaşayış binalarının ümumi sayı – 5<br />
        9 mərtəbəli – 5 bina<br />
        Mənzillərin ümumi sayı – 240<br />
        Studio tipli – 32<br />
        2 otaqlı – 36<br />
        3 otaqlı – 104<br />
        4 otaqlı – 68<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 40,1 m²<br />
        2 otaqlı – 57,7 m²<br />
        3 otaqlı – 72,9 - 76,7 m²<br />
        4 otaqlı – 99 - 114,6 m²
      </p>
    ),
  },
  {
    id: '300019',
    name: 'Binaqadi',
    center: [49.813536, 40.451634],
    polygons: [
      [
        [49.815406, 40.454590],
        [49.816947, 40.451461],
        [49.816807, 40.451379],
        [49.816562, 40.451205],
        [49.814027, 40.451215],
        [49.813536, 40.451634],
        [49.813154, 40.452549],
        [49.812687, 40.453616],
        [49.813039, 40.453719],
        [49.812941, 40.453943]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 9 hektar<br />
        Yaşayış binalarının ümumi sayı – 28<br />
        9 mərtəbəli – 16 bina<br />
        12 mərtəbəli – 12 bina<br />
        Mənzillərin ümumi sayı – 1398<br />
        Studio tipli – 32<br />
        2 otaqlı – 234<br />
        3 otaqlı – 850<br />
        4 otaqlı – 266<br />
        5 otaqlı – 16<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 40,1 m²<br />
        2 otaqlı – 57,7 m²<br />
        3 otaqlı – 72,9 - 76,7 m²<br />
        4 otaqlı – 99 - 114,6 m²<br />
        5 otaqlı – 156,5 m²<br />
        1 uşaq bağçası (80 yerlik)<br />
        1 ümumtəhsil məktəbi (960 yerlik)
      </p>
    ),
  },
  {
    id: '300015',
    name: 'Shirvan',
    center: [48.960191, 39.960226],
    polygons: [
      [
        [48.958163, 39.963442],
        [48.961312, 39.962696],
        [48.960191, 39.960226],
        [48.957078, 39.960982]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 8,11 hektar<br />
        Yaşayış binalarının ümumi sayı – 25<br />
        9 mərtəbəli – 14 bina<br />
        12 mərtəbəli – 11 bina<br />
        Mənzillərin ümumi sayı – 1 264<br />
        Studio tipli – 32<br />
        2 otaqlı – 216<br />
        3 otaqlı – 768<br />
        4 otaqlı – 248<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 40,1 m²<br />
        2 otaqlı – 57,7 m²<br />
        3 otaqlı – 72,8 - 76,7 m²<br />
        4 otaqlı – 98,7 - 114,6 m²<br />
        1 uşaq bağçası (80 yerlik)<br />
        1 ümumtəhsil məktəbi (960 yerlik)
      </p>
    ),
  },
  {
    id: '300009',
    name: 'Jabrail',
    center: [47.021569, 39.399756],
    polygons: [
      [
        [47.024024, 39.402344],
        [47.024388, 39.399679],
        [47.023479, 39.399542],
        [47.023280, 39.399482],
        [47.023162, 39.399439],
        [47.022041, 39.398819],
        [47.021841, 39.398742],
        [47.021490, 39.398649],
        [47.021484, 39.398829],
        [47.021627, 39.399358],
        [47.021633, 39.399556],
        [47.021569, 39.399756],
        [47.021480, 39.399901],
        [47.021122, 39.400367],
        [47.021057, 39.400530],
        [47.021040, 39.400747],
        [47.021080, 39.400917],
        [47.021225, 39.401113],
        [47.021380, 39.401254],
        [47.021998, 39.401757],
        [47.022262, 39.402041],
        [47.022443, 39.402290]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 7,87 hektar <br />
        Yaşayış binalarının ümumi sayı – 33<br />
        5 mərtəbəli – 7 bina<br />
        4 mərtəbəli – 26 bina<br />
        Mənzillərin ümumi sayı – 712<br />
        1 otaqlı – 8<br />
        2 otaqlı  – 136<br />
        2 otaqlı studio tipli – 8<br />
        3 otaqlı – 392<br />
        4 otaqlı – 168<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 45 - 50 m² <br />
        2 otaqlı – 55 - 90 m²<br />
        2 otaqlı studio tipli – 98 - 100 m²<br />
        3 otaqlı – 90 - 108 m²<br />
        4 otaqlı – 110 - 120 m²
      </p>
    ),
  },
  {
    id: '300018',
    name: 'Xocali',
    center: [46.798375, 39.904016],
    polygons: [
      [
        [46.800581, 39.906072],
        [46.800834, 39.904555],
        [46.799160, 39.903617],
        [46.798715, 39.904056],
        [46.798375, 39.904016],
        [46.798206, 39.904271],
        [46.798337, 39.904341],
        [46.798099, 39.904624]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 3,34 hektar<br />
        Yaşayış binalarının ümumi sayı – 15<br />
        3 mərtəbəli – 15 bina<br />
        Mənzillərin ümumi sayı – 180<br />
        2 otaqlı – 36<br />
        3 otaqlı – 90<br />
        4 otaqlı – 54<br />
      </p>
    ),
  },
  {
    id: '300014',
    name: 'Yevlax',
    center: [47.160706, 40.597264],
    polygons: [
      [
        [47.159664, 40.597184],
        [47.160242, 40.597885],
        [47.160659, 40.598382],
        [47.160894, 40.598684],
        [47.160940, 40.598665],
        [47.161185, 40.598552],
        [47.161159, 40.598499],
        [47.160930, 40.597989],
        [47.160977, 40.597971],
        [47.160706, 40.597264],
        [47.160668, 40.597175],
        [47.161231, 40.597056],
        [47.161959, 40.596916],
        [47.161825, 40.596423],
        [47.160920, 40.59655],
        [47.159990, 40.596683],
        [47.160096, 40.597032],
        [47.159673, 40.597112],
        [47.159710, 40.597157]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 1,9 hektar<br />
        Yaşayış binalarının ümumi sayı – 6<br />
        7 mərtəbəli – 6 bina<br />
        Mənzillərin ümumi sayı – 329<br />
        Studio tipli – 14<br />
        1 otaqlı – 28<br />
        2 otaqlı – 84<br />
        3 otaqlı – 139<br />
        4 otaqlı – 64<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 43,2 m²<br />
        1 otaqlı – 45,5 m²<br />
        2 otaqlı – 58,5 - 86,5 m²<br />
        3 otaqlı – 74,2 - 98,1 m²<br />
        4 otaqlı – 102,4 - 129,2 m²
      </p>
    ),
  },
  {
    id: '300008',
    name: 'Fuzuli',
    center: [47.149727, 39.599051],
    polygons: [
      [
        [47.150185, 39.600664],
        [47.154170, 39.599995],
        [47.153735, 39.598742],
        [47.149871, 39.599561],
        [47.150185, 39.600664]
      ],
      [
        [47.149727, 39.599051],
        [47.153557, 39.598241],
        [47.153225, 39.597310],
        [47.152842, 39.597335],
        [47.152493, 39.597360],
        [47.152145, 39.597393],
        [47.151797, 39.597436],
        [47.151031, 39.597540],
        [47.150128, 39.597719],
        [47.149689, 39.597835],
        [47.149412, 39.597903],
        [47.149727, 39.599051]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 8,9 hektar<br />
        Yaşayış binalarının ümumi sayı – 38<br />
        4 mərtəbəli – 2 bina<br />
        4-5 mərtəbəli – 10 bina  <br />
        5 mərtəbəli – 16 bina  <br />
        6 mərtəbəli – 4 bina <br />
        5-6 mərtəbəli – 2 bina <br />
        6-7 mərtəbəli – 4 bina <br />
        Mənzillərin ümumi sayı – 846<br />
        2 otaqlı – 318<br />
        3 otaqlı – 430<br />
        4 otaqlı – 98<br />
        Mənzillərin ölçüləri:<br />
        2 otaqlı – 54,2 - 68,3 m²<br />
        3 otaqlı – 71,6 - 109,3 m²<br />
        4 otaqlı – 92,8 - 108,4 m²
      </p>
    ),
  },
  {
    id: '300022',
    name: 'Saray',
    center: [49.742444, 40.527421],
    polygons: [
      [
        [49.744917, 40.526992],
        [49.744916, 40.526992],
        [49.744775, 40.527001],
        [49.744221, 40.527772],
        [49.743227, 40.527796],
        [49.743209, 40.527389],
        [49.742724, 40.527408],
        [49.742444, 40.527421],
        [49.741966, 40.527436],
        [49.741713, 40.527785],
        [49.741264, 40.528405],
        [49.740910, 40.528893],
        [49.742130, 40.529449],
        [49.742926, 40.529812],
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 4,5 hektar<br />
        Yaşayış binalarının ümumi sayı – 12<br />
        12 mərtəbəli – 6 bina<br />
        16 mərtəbəli – 6 bina<br />
        Mənzillərin ümumi sayı – 864<br />
        Studio tipli – 56<br />
        2 otaqlı – 216<br />
        3 otaqlı – 422<br />
        4 otaqlı – 106<br />
        5 otaqlı – 64<br />
        Mənzillərin ölçüləri:<br />
        Studio tipli – 44,50 - 45,50 m²<br />
        2 otaqlı – 60,50 - 63,77 m²<br />
        3 otaqlı – 79,47 - 83,33 m²<br />
        4 otaqlı – 96,54 - 103,39 m²<br />
        5 otaqlı – 118,10 - 120,84 m²<br />
        1 uşaq bağçası (80 yerlik)
      </p>
    ),
  },
  {
    id: '300023',
    name: 'Hovsan 231',
    center: [50.074260, 40.382081],
    polygons: [
      [
        [50.073524, 40.381979],
        [50.074260, 40.382081],
        [50.075282, 40.378854],
        [50.074538, 40.378794]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 2,31 hektar<br />
        Yaşayış binalarının ümumi sayı – 5<br />
        10 mərtəbəli – 5 bina<br />
        Mənzillərin ümumi sayı – 225<br />
        2 otaqlı – 90<br />
        3 otaqlı – 135<br />
        Mənzillərin ölçüləri:<br />
        2 otaqlı – 58,84 m²<br />
        3 otaqlı – 84,41 - 95,47 m²
      </p>
    ),
  },
  {
    id: '300025',
    name: 'Naxcivan',
    center: [45.409882, 39.186780],
    polygons: [
      [
        [45.406800, 39.187045],
        [45.407673, 39.187398],
        [45.407836, 39.187416],
        [45.408125, 39.187394],
        [45.408703, 39.187280],
        [45.409204, 39.187208],
        [45.409203, 39.187085],
        [45.409394, 39.187084],
        [45.409366, 39.185677],
        [45.409683, 39.185679],
        [45.409696, 39.186781],
        [45.409882, 39.186780],
        [45.409843, 39.185522],
        [45.409365, 39.185534],
        [45.409358, 39.185380],
        [45.409826, 39.185367],
        [45.409808, 39.184711],
        [45.409664, 39.184712],
        [45.409659, 39.184554],
        [45.409760, 39.184552],
        [45.409746, 39.183902],
        [45.410180, 39.183895],
        [45.410169, 39.183750],
        [45.410145, 39.183679],
        [45.410074, 39.183579],
        [45.409999, 39.183518],
        [45.409177, 39.184323],
        [45.409518, 39.184536],
        [45.409087, 39.184951],
        [45.408752, 39.184739],
        [45.408147, 39.185372],
        [45.408299, 39.185417],
        [45.408358, 39.185500],
        [45.408351, 39.186737],
        [45.408048, 39.186735],
        [45.408041, 39.185717],
        [45.408276, 39.185715],
        [45.408277, 39.185508],
        [45.408242, 39.185462],
        [45.408100, 39.185427],
        [45.407432, 39.186210],
        [45.406800, 39.187045]
      ],
      [
        [45.409974, 39.186781],
        [45.410281, 39.186770],
        [45.410276, 39.185687],
        [45.410179, 39.185688],
        [45.410199, 39.186698],
        [45.409972, 39.186701],
        [45.409974, 39.186781]
      ],
      [
        [45.409926, 39.185610],
        [45.410271, 39.185614],
        [45.410262, 39.184984],
        [45.410233, 39.184528],
        [45.410149, 39.184529],
        [45.410168, 39.185536],
        [45.409921, 39.185539],
        [45.409926, 39.185610]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 4,98 hektar<br />
        Yaşayış binalarının ümumi sayı – 16<br />
        6-7 mərtəbəli – 14 bina<br />
        7 mərtəbəli – 1 bina<br />
        6 mərtəbəli – 1 bina<br />
        Mənzillərin ümumi sayı – 518<br />
        1 otaqlı – 93<br />
        2 otaqlı – 37<br />
        3 otaqlı – 351<br />
        4 otaqlı – 37<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 40 - 45 m²<br />
        2 otaqlı – 60 - 65 m²<br />
        3 otaqlı – 75 - 95 m²<br />
        4 otaqlı – 95 - 105 m²<br />
        1 uşaq bağçası (80 yerlik)
      </p>
    ),
  },
  {
    id: '300026',
    name: 'Shamkir',
    center: [46.026544, 40.852634],
    polygons: [
      [
        [46.023775, 40.852421],
        [46.024653, 40.853021],
        [46.025251, 40.852562],
        [46.025280, 40.852582],
        [46.025405, 40.852481],
        [46.026190, 40.853086],
        [46.026206, 40.853068],
        [46.026383, 40.852841],
        [46.026544, 40.852634],
        [46.026676, 40.852462],
        [46.026821, 40.852276],
        [46.027027, 40.852016],
        [46.027159, 40.851845],
        [46.027265, 40.851703],
        [46.027400, 40.851525],
        [46.027535, 40.851351],
        [46.027617, 40.851241],
        [46.027691, 40.851139],
        [46.027764, 40.851036],
        [46.027867, 40.850894],
        [46.027961, 40.850763],
        [46.028126, 40.850547],
        [46.028005, 40.850324]
      ],
      [
        [46.026242, 40.853112],
        [46.026257, 40.853124],
        [46.027688, 40.852369],
        [46.028662, 40.851829],
        [46.028110, 40.850566],
        [46.027981, 40.850767],
        [46.027887, 40.850900],
        [46.027785, 40.851042],
        [46.027712, 40.851146],
        [46.027639, 40.851248],
        [46.027559, 40.851360],
        [46.027427, 40.851533],
        [46.027296, 40.851713],
        [46.027192, 40.851856],
        [46.027061, 40.852028],
        [46.026861, 40.852289],
        [46.026722, 40.852476],
        [46.026592, 40.852650],
        [46.026437, 40.852858],
        [46.026262, 40.853086]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 5,76 hektar<br />
        Yaşayış binalarının ümumi sayı – 13<br />
        7 mərtəbəli – 13 bina<br />
        Mənzillərin ümumi sayı – 555<br />
        1 otaqlı – 80<br />
        2 otaqlı – 210<br />
        3 otaqlı – 230<br />
        4 otaqlı – 35<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 39,9 m²<br />
        2 otaqlı – 58,1 - 59,9 m²<br />
        3 otaqlı – 79,3 - 79,7 m²<br />
        4 otaqlı – 109,5 m²<br />
        1 uşaq bağçası (80 yerlik)
      </p>
    ),
  },
  {
    id: '300027',
    name: 'Salyan',
    center: [48.965522, 39.576617],
    polygons: [
      [
        [48.963842, 39.582504],
        [48.965522, 39.576617],
        [48.963298, 39.573794],
        [48.958542, 39.580507]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 30 hektar<br />
        Yaşayış binalarının ümumi sayı – 115<br />
        3-4 mərtəbəli – 56 bina<br />
        3 mərtəbəli – 44 bina<br />
        4 mərtəbəli – 15 bina<br />
        Mənzillərin ümumi sayı – 2033<br />
        1 otaqlı – 109<br />
        2 otaqlı – 609<br />
        3 otaqlı – 1065<br />
        4 otaqlı – 250<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 41 - 43,05 m²<br />
        2 otaqlı – 57,84 - 64,11 m²<br />
        3 otaqlı – 78,29 - 86,08 m²<br />
        4 otaqlı – 100,26 m²<br />
        1 uşaq bağçası (240 yerlik)<br />
        1 ümumtəhsil məktəbi (960 yerlik)
      </p>
    ),
  },
  {
    id: '300024',
    name: 'Hovsan 3',
    center: [50.070513, 40.381561],
    polygons: [
      [
        [50.069055, 40.383571],
        [50.069394, 40.383065],
        [50.070377, 40.381754],
        [50.070513, 40.381561],
        [50.068747, 40.381316],
        [50.069069, 40.381809],
        [50.068365, 40.382117]
      ],
      [
        [50.069055, 40.383571],
        [50.071423, 40.383831],
        [50.072013, 40.381989],
        [50.070377, 40.381754],
        [50.069394, 40.383065]
      ]
    ],
    info: (
      <p>
        Ərazinin ümumi sahəsi – 5,83 hektar<br />
        Yaşayış binalarının ümumi sayı – 15<br />
        12 mərtəbəli – 4 bina<br />
        14 mərtəbəli – 11 bina<br />
        Mənzillərin ümumi sayı – 1 154<br />
        1 otaqlı – 240<br />
        2 otaqlı – 342<br />
        3 otaqlı – 465<br />
        4 otaqlı – 107<br />
        Mənzillərin ölçüləri:<br />
        1 otaqlı – 37,50 - 46,20 m²<br />
        2 otaqlı – 61,70 - 66,50 m²<br />
        3 otaqlı – 78,10 m - 93,80 m²<br />
        4 otaqlı – 100,40 - 106,50 m²
      </p>
    ),
  },
];

// Ids coming from the API may be numbers, the <select> always gives a string.
const findProject = (id) => PROJECTS.find(project => String(project.id) === String(id));

// One GeoJSON feature per polygon of the project.
const toFeatureCollection = (project) => ({
  type: 'FeatureCollection',
  features: project.polygons.map(coordinates => ({
    type: 'Feature',
    properties: { name: project.name },
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  })),
});

const ProjectPopup = ({ project }) => {
  if (!project || !project.info) return null;

  return <div className='mapinfo-popup'>{project.info}</div>;
};

const Map = ({ projects }) => {
  const mapRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const mapInstance = new mapboxgl.Map({
      container: 'map',
      style: MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM
    });

    mapInstance.on('load', function () {
      PROJECTS.forEach((project, index) => {
        mapInstance.addSource(project.id, {
          type: 'geojson',
          data: toFeatureCollection(project),
        });

        mapInstance.addLayer({
          id: `${project.id}-zone`,
          type: 'fill',
          source: project.id,
          layout: {},
          paint: {
            'fill-color': project.color || PALETTE[index % PALETTE.length],
            'fill-opacity': FILL_OPACITY,
          }
        });

        // clicking a polygon selects its project (dropdown + info popup follow)
        mapInstance.on('click', `${project.id}-zone`, () => {
          setSelectedId(project.id);
        });
        mapInstance.on('mouseenter', `${project.id}-zone`, () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });
        mapInstance.on('mouseleave', `${project.id}-zone`, () => {
          mapInstance.getCanvas().style.cursor = '';
        });
      });
    });

    mapRef.current = mapInstance;

    return () => {
      mapInstance.remove();
      mapRef.current = null;
    };
  }, []);

  const handleRegionChange = (e) => {
    const selectedRegion = e.target.value;
    const project = selectedRegion === '-' ? null : findProject(selectedRegion);

    setSelectedId(selectedRegion === '-' ? null : selectedRegion);

    if (!mapRef.current) return;

    mapRef.current.flyTo({
      center: project ? project.center : DEFAULT_CENTER,
      zoom: project ? PROJECT_ZOOM : DEFAULT_ZOOM,
      bearing: 0,
      speed: 0.8,
      curve: 2,
      easing: (t) => t
    });
  };

  return (
    <>
      <div id="projects-map" className='position-relative mt-5 pt-5 pt-lg-0 mt-lg-0'>
        <div className='proj-buttons w-100 d-flex justify-content-between position-absolute flex-wrap-reverse col-12 col-lg-6 mt-5 pt-2 px-3 top-0 left-0 z-3'>
          <select className="region-selector cursor ps-4 mt-3 mt-lg-0 col-lg-6 col-12" value={selectedId || '-'} onChange={handleRegionChange}>
            <option value="-">Layihə</option>
            {projects.map(proj =>
              <option key={proj.id} value={proj.id}>{proj.name_az}</option>
            )}
          </select>
          <div className='d-flex position-relative proj-buttons mt-4 pe-4'>
            <Link to='/layiheler-xarite' className='btn btn-secondary me-3'>Xəritə üzrə</Link>
            <Link to='/layiheler' className='btn btn-secondary me-3'>Siyahı üzrə</Link>
          </div>
        </div>

        <div id="map" className='mb-0' style={{ width: '100%', height: '700px' }}></div>

        <ProjectPopup project={selectedId ? findProject(selectedId) : null} />
      </div>
    </>
  );
};

export default Map;
